import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { getInventoryCountEntry, deleteInventoryCountEntry } from '../../services/inventoryCountService';
import { InventoryCountEntry } from '../../types/inventoryCount';

const InventoryCountEntryDetails: React.FC = () => {
  const { entryId } = useParams<{ entryId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [entry, setEntry] = useState<InventoryCountEntry | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);

  // جلب تفاصيل أمر الإدخال
  useEffect(() => {
    const fetchEntryDetails = async () => {
      if (!entryId) return;
      
      setLoading(true);
      try {
        const response = await getInventoryCountEntry(entryId);
        if (response.success) {
          setEntry(response.entry);
        } else {
          setError('حدث خطأ أثناء جلب بيانات أمر الإدخال');
        }
      } catch (err) {
        console.error('Error fetching inventory count entry details:', err);
        setError('حدث خطأ أثناء الاتصال بالخادم');
      } finally {
        setLoading(false);
      }
    };

    fetchEntryDetails();
  }, [entryId]);

  // التعامل مع حذف أمر الإدخال
  const handleDelete = async () => {
    if (!entry || !entryId) return;
    
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    
    setDeleting(true);
    try {
      const response = await deleteInventoryCountEntry(entryId);
      if (response.success) {
        // الانتقال إلى صفحة تفاصيل طلب الجرد بعد الحذف
        navigate(`/inventory-count/${entry.requestId}`);
      } else {
        setError('حدث خطأ أثناء حذف أمر الإدخال');
      }
    } catch (err) {
      console.error('Error deleting inventory count entry:', err);
      setError('حدث خطأ أثناء الاتصال بالخادم');
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  // التنقل إلى صفحة تعديل أمر الإدخال
  const handleEdit = () => {
    if (!entryId) return;
    navigate(`/inventory-count/entries/${entryId}/edit`);
  };

  // التنقل إلى صفحة تفاصيل طلب الجرد
  const handleBack = () => {
    if (!entry) return;
    navigate(`/inventory-count/${entry.requestId}`);
  };

  // عرض حالة التحميل
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5 }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          جاري تحميل بيانات أمر الإدخال...
        </Typography>
      </Box>
    );
  }

  // عرض رسالة في حالة عدم وجود أمر إدخال
  if (!entry) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          لم يتم العثور على أمر الإدخال المطلوب
        </Alert>
        <Button 
          variant="outlined" 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/inventory-count')}
        >
          العودة إلى قائمة طلبات الجرد
        </Button>
      </Box>
    );
  }

  // عرض سمات المتغير كنص
  const formatVariantAttributes = (attributes?: Record<string, string>) => {
    if (!attributes) return '';
    
    return Object.entries(attributes)
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');
  };

  return (
    <Box>
      {/* رسالة الخطأ */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* رسالة تأكيد الحذف */}
      {confirmDelete && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          هل أنت متأكد من رغبتك في حذف أمر الإدخال هذا؟ لا يمكن التراجع عن هذا الإجراء.
          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Button 
              variant="contained" 
              color="error" 
              onClick={handleDelete}
              disabled={deleting}
              size="small"
            >
              {deleting ? 'جاري الحذف...' : 'تأكيد الحذف'}
            </Button>
            <Button 
              variant="outlined" 
              onClick={() => setConfirmDelete(false)}
              size="small"
            >
              إلغاء
            </Button>
          </Box>
        </Alert>
      )}

      {/* معلومات أمر الإدخال */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          backgroundColor: 'background.paper'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            تفاصيل أمر الإدخال - {entry.entryNumber}
          </Typography>
          <Box>
            <Tooltip title="تعديل أمر الإدخال">
              <IconButton color="primary" onClick={handleEdit} sx={{ mr: 1 }}>
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="حذف أمر الإدخال">
              <IconButton color="error" onClick={handleDelete} disabled={deleting || confirmDelete}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
        <Divider sx={{ mb: 2 }} />
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              رقم أمر الإدخال: <Typography component="span" fontWeight="bold">{entry.entryNumber}</Typography>
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              الحالة: <Chip
                size="small"
                label="نشط"
                color="success"
              />
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              تاريخ الإنشاء: <Typography component="span" fontWeight="bold">
                {new Date(entry.createdAt).toLocaleString('ar-SA')}
              </Typography>
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              آخر تحديث: <Typography component="span" fontWeight="bold">
                {new Date(entry.updatedAt).toLocaleString('ar-SA')}
              </Typography>
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* قائمة المنتجات */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          backgroundColor: 'background.paper'
        }}
      >
        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
          المنتجات المضافة
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <TableContainer sx={{ mb: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>اسم المنتج</TableCell>
                <TableCell>متغير المنتج</TableCell>
                <TableCell>الباركود</TableCell>
                <TableCell>الوحدة</TableCell>
                <TableCell>الكمية</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entry.items.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{item.productName}</TableCell>
                  <TableCell>
                    {formatVariantAttributes(item.variantAttributes as Record<string, string>) || '-'}
                  </TableCell>
                  <TableCell>{item.barcode || '-'}</TableCell>
                  <TableCell>{item.unitName}</TableCell>
                  <TableCell>{item.quantity}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* إجمالي العناصر */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Chip
            label={`إجمالي العناصر: ${entry.items.length}`}
            color="primary"
            variant="outlined"
          />
        </Box>
      </Paper>

      {/* ملاحظات */}
      {entry.notes && (
        <Paper
          elevation={3}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 2,
            backgroundColor: 'background.paper'
          }}
        >
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            ملاحظات
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body1">{entry.notes}</Typography>
        </Paper>
      )}

      {/* زر الرجوع */}
      <Box sx={{ mt: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          العودة إلى تفاصيل طلب الجرد
        </Button>
      </Box>
    </Box>
  );
};

export default InventoryCountEntryDetails;